import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin, Map as MapIcon } from "lucide-react";

import { SIGNBOARDS, REGIONS } from "@/data/signboards";

/**
 * SignboardsShowcase — small clickable photo grid of every NoJo
 * Enterprise outdoor signboard placement across Japan.
 *
 * Mounted on:
 *   1. Homepage — surfaces the outdoor presence story between editorial
 *      bands. Owner wants visitors to immediately know we have
 *      physical signboards across Japan, and click straight to any
 *      one of them.
 *   2. JapanETCcard service detail page — every board pictured here
 *      currently advertises JapanETCcard, so the section doubles as
 *      proof-of-presence + a direct nav route into each detail page.
 *
 * Variants:
 *   - "home" — full editorial band with eyebrow / headline / 4-up grid /
 *     "View hub" CTA. Used on `/`.
 *   - "service" — tighter band intended to sit inside an existing
 *     service detail page (no aura, no large headline, smaller copy).
 *
 * Every card uses the FIRST gallery photo as its hero shot — same
 * source-of-truth as the signboards hub, so adding a new board to
 * /data/signboards.js automatically appears here.
 */

function regionMeta(key) {
  return REGIONS.find((r) => r.key === key) || { accent: "#0F1D2D" };
}

function BoardCard({ board, lang, isJa, testIdPrefix }) {
  const region = regionMeta(board.region);
  const hero = board.photos && board.photos.length > 0 ? board.photos[0] : null;
  return (
    <Link
      to={`/${lang}/signboards/${board.slug}`}
      data-testid={`${testIdPrefix}-card-${board.slug}`}
      className="group relative block rounded-2xl overflow-hidden ring-1 ring-[#E2E8F0] bg-white hover:ring-[#0F1D2D]/30 hover:shadow-[0_18px_44px_-18px_rgba(15,29,45,0.32)] hover:-translate-y-0.5 transition-all duration-200"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-[#F1F5F9]">
        {hero ? (
          <img
            src={hero}
            alt={
              isJa
                ? `${board.title.ja}の現地写真`
                : `${board.title.en} on-site photo`
            }
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              e.currentTarget.nextSibling.style.display = "flex";
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#94A3B8]">
            <MapIcon className="h-8 w-8" strokeWidth={1.5} />
          </div>
        )}
        {hero && (
          <div className="hidden w-full h-full flex items-center justify-center text-[#94A3B8] bg-[#F1F5F9] absolute inset-0">
            <MapIcon className="h-8 w-8" strokeWidth={1.5} />
          </div>
        )}
        {/* Gradient veil for legible label */}
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#5B2E83]/85 via-[#4A2570]/30 to-transparent"
        />
        {/* Region pill, top-left */}
        <span
          className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[9.5px] tracking-[0.2em] uppercase font-bold text-white px-2 py-1 rounded-full ring-1 ring-white/30 backdrop-blur-sm"
          style={{ backgroundColor: region.accent }}
        >
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-white" />
          {isJa ? region.ja : region.en}
        </span>
        {/* Route badge, top-right */}
        {board.route && (
          <span className="absolute top-3 right-3 inline-flex items-center text-[9.5px] tracking-[0.16em] uppercase font-bold text-[#0F1D2D] bg-white/95 px-2 py-1 rounded-full ring-1 ring-[#E2E8F0]">
            {board.route}
          </span>
        )}
        {/* Bottom label */}
        <div className="absolute inset-x-0 bottom-0 p-3.5 text-white">
          <div className="text-[14px] md:text-[15px] font-display font-bold tracking-tight leading-tight">
            {board.title[lang]}
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-white/85">
            <MapPin className="h-3 w-3 shrink-0" strokeWidth={2} />
            <span className="truncate">{board.location[lang]}</span>
          </div>
        </div>
      </div>
      {/* Footer chevron strip */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-white">
        <span
          className="text-[10.5px] tracking-[0.22em] uppercase font-bold"
          style={{ color: region.accent }}
        >
          {isJa ? "詳細を見る" : "View detail"}
        </span>
        <ArrowUpRight
          className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          strokeWidth={2}
          style={{ color: region.accent }}
        />
      </div>
    </Link>
  );
}

export default function SignboardsShowcase({
  lang,
  variant = "home",
  testIdPrefix,
}) {
  const isJa = lang === "ja";
  const prefix = testIdPrefix || `signboards-showcase-${variant}`;
  const boards = SIGNBOARDS;
  const okinawaCount = boards.filter((b) => b.region === "okinawa").length;
  const yokosukaCount = boards.filter((b) => b.region === "yokosuka").length;

  if (variant === "service") {
    // Compact, single-section band for use inside a service detail page.
    return (
      <section
        data-testid={prefix}
        className="bg-white border-b border-[#E2E8F0]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-12 md:py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 md:gap-10 mb-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] font-mono uppercase font-bold text-[#5B2E83] mb-3">
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#5B2E83]" />
                {isJa ? "屋外でのプレゼンス" : "On the road"}
              </div>
              <h2
                className={[
                  isJa ? "font-ja" : "font-display",
                  "text-[#0F1D2D] text-2xl md:text-3xl lg:text-[34px] font-bold tracking-tight leading-tight",
                ].join(" ")}
              >
                {isJa
                  ? "JapanETCcardの看板は、日本各地で目にすることができます。"
                  : "You'll see JapanETCcard signboards across Japan."}
              </h2>
              <p className="mt-3 text-[14px] md:text-[15px] leading-[1.75] text-[#475569]">
                {isJa
                  ? `沖縄${okinawaCount}区画・横須賀${yokosukaCount}区画。米軍基地・幹線道路・通勤動線に沿って設置されています。各看板をタップすると、現地写真とGoogleストリートビューがご覧いただけます。`
                  : `${okinawaCount} placements in Okinawa, ${yokosukaCount} at our Yokosuka HQ — positioned along U.S. base perimeters, primary highways and commuter corridors. Tap any board to see on-site photos and live Google Street View.`}
              </p>
            </div>
            <Link
              to={`/${lang}/signboards`}
              data-testid={`${prefix}-hub-cta`}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#5B2E83] hover:bg-[#3F1F5B] text-white text-[12.5px] font-semibold tracking-tight transition-colors shrink-0"
            >
              {isJa ? "看板ハブを見る" : "View signboards hub"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {boards.map((b) => (
              <BoardCard
                key={b.slug}
                board={b}
                lang={lang}
                isJa={isJa}
                testIdPrefix={prefix}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Homepage variant — full editorial band with stronger headline.
  return (
    <section
      data-testid={prefix}
      className="relative isolate bg-[#F8F9FA] border-t border-[#E2E8F0] overflow-hidden"
    >
      {/* Soft brand bloom backdrop */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-16 right-0 w-[420px] h-[420px] rounded-full opacity-[0.07] blur-3xl"
        style={{ backgroundColor: "#5B2E83" }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute top-32 left-12 w-[320px] h-[320px] rounded-full opacity-[0.06] blur-3xl"
        style={{ backgroundColor: "#2F8F3C" }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-10 md:mb-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] font-mono uppercase font-bold text-[#5B2E83] mb-4">
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#5B2E83]" />
              {isJa ? "屋外でのプレゼンス" : "Outdoor presence"}
            </div>
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-3xl md:text-4xl lg:text-[44px] font-bold tracking-tighter leading-[1.1]",
              ].join(" ")}
            >
              {isJa
                ? "私たちの看板は、日本各地のドライバーの目にとまります。"
                : "Our signboards meet drivers across Japan."}
            </h2>
            <p className="mt-5 text-[15px] md:text-[16px] leading-[1.8] text-[#475569] max-w-2xl">
              {isJa
                ? `沖縄の国道58号線沿いに${okinawaCount}区画、横須賀の本社拠点に${yokosukaCount}区画。米軍基地・幹線道路・通勤動線にあわせて配置されています。各看板の写真とGoogleストリートビューでご確認ください。`
                : `${okinawaCount} placements along Route 58 in Okinawa and ${yokosukaCount} at our Yokosuka HQ — positioned along U.S. base perimeters, primary highways and commuter corridors. Tap any board to step into its on-site gallery and Google Street View.`}
            </p>
          </div>
          <div className="lg:col-span-5 flex lg:items-end lg:justify-end gap-3 flex-wrap">
            <Link
              to={`/${lang}/signboards`}
              data-testid={`${prefix}-hub-cta`}
              className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-[#5B2E83] hover:bg-[#3F1F5B] text-white text-[13px] font-semibold tracking-tight transition-colors shadow-[0_12px_28px_-12px_rgba(91,46,131,0.55)]"
            >
              {isJa ? "看板ハブを見る" : "Browse all signboards"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
            </Link>
            <Link
              to={`/${lang}/signboards/map/okinawa`}
              data-testid={`${prefix}-map-cta`}
              className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-white hover:bg-[#F1F5F9] text-[#0F1D2D] text-[13px] font-semibold tracking-tight transition-colors ring-1 ring-[#E2E8F0]"
            >
              <MapIcon className="h-3.5 w-3.5" strokeWidth={2} style={{ color: "#2F8F3C" }} />
              {isJa ? "詳細マップ" : "Detailed map"}
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {boards.map((b) => (
            <BoardCard
              key={b.slug}
              board={b}
              lang={lang}
              isJa={isJa}
              testIdPrefix={prefix}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
